// The Reports screen's state in its URL (spec/13 §15, F-ANL-03): the tab, the range and the scope, so a report is a
// link a manager can share — `/v/<venue>/reports?tab=sessions&range=lastMonth&venue=all`.
//
//   tab     overview | revenue | sessions | products | wallet | staff | games | daily  (default overview)
//   range   a preset (today, yesterday, last7, last30, thisMonth, lastMonth)        (default last7)
//           or `custom` with `from` and `to`, business days `YYYY-MM-DD`, 1 … 92 of them
//   venue   absent: this venue · `all`: every venue the member works at, rolled up · a venue id: that venue
//   day     the Daily summary's business day, `YYYY-MM-DD` (owners only); absent: the latest day ended everywhere
//
// A preset is resolved by the server in the venue's timezone and business-day cutoff (`GET …/reports/range`), so
// "Today" at 02:00 is still last night's business day. The date maths here is `@clavian/domain/reports`' — imported by
// its narrow sub-path, never the package root (whose contracts runtime the console's CSP forbids).

import { isReportDate, isReportPreset, previousRangeOf, REPORT_PRESETS, REPORT_RANGE_MAX_DAYS, reportRangeDays, type ReportPreset } from '@clavian/domain/reports';

export const REPORT_TABS = ['overview', 'revenue', 'sessions', 'products', 'wallet', 'staff', 'games', 'daily'] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

export const DEFAULT_PRESET: ReportPreset = 'last7';

export { REPORT_PRESETS, type ReportPreset };

/** The search as the URL carries it, every key optional and checked. */
export interface ReportSearch {
  readonly tab?: ReportTab;
  readonly range?: ReportPreset | 'custom';
  readonly from?: string;
  readonly to?: string;
  readonly venue?: string;
  /** The Daily summary's business day. */
  readonly day?: string;
}

/** The route's `validateSearch`: keeps what is well formed, drops the rest. */
export function readReportSearch(raw: Record<string, unknown>): ReportSearch {
  const text = (key: string): string | undefined => (typeof raw[key] === 'string' ? (raw[key] as string) : undefined);
  const tab = text('tab');
  const range = text('range');
  const from = text('from');
  const to = text('to');
  const venue = text('venue');
  const day = text('day');
  return {
    ...(tab !== undefined && (REPORT_TABS as readonly string[]).includes(tab) ? { tab: tab as ReportTab } : {}),
    ...(range !== undefined && (range === 'custom' || isReportPreset(range)) ? { range: range as ReportPreset | 'custom' } : {}),
    ...(from !== undefined && isReportDate(from) ? { from } : {}),
    ...(to !== undefined && isReportDate(to) ? { to } : {}),
    ...(venue !== undefined && (venue === 'all' || /^ven_[0-9a-z]{26}$/.test(venue)) ? { venue } : {}),
    ...(day !== undefined && isReportDate(day) ? { day } : {}),
  };
}

/** What the range asks for: a preset to resolve, or a custom range already in business days. */
export type RangeChoice =
  | { readonly kind: 'preset'; readonly preset: ReportPreset }
  | { readonly kind: 'custom'; readonly from: string; readonly to: string; readonly previousFrom: string; readonly previousTo: string }
  | { readonly kind: 'invalid'; readonly from: string | undefined; readonly to: string | undefined };

export function rangeChoiceOf(search: ReportSearch): RangeChoice {
  if (search.range !== 'custom') return { kind: 'preset', preset: search.range ?? DEFAULT_PRESET };
  const { from, to } = search;
  if (from === undefined || to === undefined) return { kind: 'invalid', from, to };
  const days = reportRangeDays({ from, to });
  if (days < 1 || days > REPORT_RANGE_MAX_DAYS) return { kind: 'invalid', from, to };
  const previous = previousRangeOf({ from, to });
  return { kind: 'custom', from, to, previousFrom: previous.from, previousTo: previous.to };
}

/** The search with a change applied, without empty keys (so the URL stays short and stable). */
export function nextSearch(current: ReportSearch, patch: Partial<Record<keyof ReportSearch, string | undefined>>): Record<string, string> {
  const merged: Record<string, string | undefined> = { ...current, ...patch };
  // A preset needs no dates; a default needs no key.
  if (merged['range'] !== 'custom') {
    delete merged['from'];
    delete merged['to'];
  }
  if (merged['range'] === DEFAULT_PRESET) delete merged['range'];
  if (merged['tab'] === 'overview') delete merged['tab'];
  // A business day belongs to the Daily summary alone.
  if (merged['tab'] !== 'daily') delete merged['day'];
  return Object.fromEntries(Object.entries(merged).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== ''));
}

export { REPORT_RANGE_MAX_DAYS };
